import React, { useEffect, useMemo, useState } from "react";
import BackButton from "@/components/BackButton";
import { usePinCounts } from "@/lib/usePinCounts";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fetchProducts, fetchServices, fetchShops, fetchHiring, fetchHire } from "@/lib/api";
import JobFilters, { filterJobs } from "@/components/JobFilters";
import { useSavedJobs } from "@/lib/savedJobs";
import { ProductCard, ServiceCard, ShopCard, HireCard } from "@/components/Cards";
import RadiusFilter from "@/components/RadiusFilter";
import { ArrowLeft, Plus } from "lucide-react";

const TITLES = {
  products: { title: "All Products", hindi: "Sabhi Saaman" },
  services: { title: "All Services", hindi: "Sabhi Sewaayein" },
  hiring: { title: "All Jobs", hindi: "Sabhi Naukriyaan" },
  shops: { title: "Local Shops", hindi: "Sabhi Dukaanein" },
};

export default function CategoryPage() {
  const { type } = useParams();
  const { pincode, radiusKm, user } = useApp();
  const pinCounts = usePinCounts(pincode, radiusKm);
  const navigate = useNavigate();
  const postJob = () => (user ? navigate("/onboarding?open=employer&post=1") : window.dispatchEvent(new Event("vb:open-auth")));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchParams] = useSearchParams();
  const sharedId = type === "hiring" ? (searchParams.get("job") || "").trim() : "";
  const [shared, setShared] = useState(null);
  const [jf, setJfRaw] = useState(() => { const urlKm = Number(searchParams.get("km")); const km = [5, 10, 25].includes(urlKm) ? urlKm : Number(localStorage.getItem("vb_jobs_km")); return { type: "", unit: "", fresher: false, saved: false, km: [5, 10, 25].includes(km) ? km : 0 }; });
  const setJf = (next) => setJfRaw((prev) => {
    const v = typeof next === "function" ? next(prev) : next;
    if (v.km !== prev.km) { try { v.km ? localStorage.setItem("vb_jobs_km", String(v.km)) : localStorage.removeItem("vb_jobs_km"); } catch { /* storage blocked */ } }
    return v;
  });
  const savedIds = useSavedJobs(user?.id);
  const jfEff = useMemo(() => (pincode ? jf : { ...jf, km: 0 }), [jf, pincode]);

  useEffect(() => {
    if (!sharedId) { setShared(null); return; }
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(sharedId)) { setShared("missing"); return; }
    fetchHire(sharedId).then(setShared).catch(() => setShared("missing"));
  }, [sharedId]);
  useEffect(() => {
    if (shared && shared !== "missing") setTimeout(() => document.querySelector('[data-testid="shared-job"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [shared]);
  const shownItems = useMemo(() => (type === "hiring" ? filterJobs(items, jfEff, savedIds).filter((j) => !shared || j.id !== shared.id) : items), [type, items, jfEff, shared, savedIds]);

  useEffect(() => {
    setLoading(true);
    const fetcher = { products: fetchProducts, services: fetchServices, hiring: fetchHiring, shops: fetchShops }[type];
    if (!fetcher) { setLoading(false); return; }
    const useRadius = type !== "hiring";
    fetcher({ pincode, radius_km: useRadius ? (radiusKm ?? undefined) : undefined, limit: 50 }).then((d) => { setItems(d); setLoadError(false); }).catch(() => setLoadError(true)).finally(() => setLoading(false));
  }, [type, pincode, radiusKm, reloadKey]);

  const meta = TITLES[type] || { title: "Category", hindi: "" };

  const Card = { products: ProductCard, services: ServiceCard, hiring: HireCard, shops: ShopCard }[type];
  const gridCols = type === "products" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" :
                   type === "services" ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" :
                   type === "hiring" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" :
                   "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  const renderItem = (item) => {
    if (type === "products") return <ProductCard key={item.id} product={item} />;
    if (type === "services") return <ServiceCard key={item.id} service={item} />;
    if (type === "hiring") return <HireCard key={item.id} job={item} />;
    if (type === "shops") return <ShopCard key={item.id} shop={item} />;
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid={`category-page-${type}`}>
      <BackButton className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6" testid="category-back-btn" />
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700 mb-2">{meta.hindi}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">{meta.title}</h1>
          {pinCounts && (type === "products" || type === "services") && <div className="mt-1.5 text-sm text-slate-700" data-testid="category-pin-count"><span className="font-semibold">{pinCounts[type]}</span> {pinCounts[type] === 1 ? type.slice(0, -1) : type} in {pinCounts.pincode}</div>}
          <div className="text-sm text-slate-500 mt-1" data-testid="category-results-count">Showing {shownItems.length} {shownItems.length === 1 ? "result" : "results"}{pincode ? <> near pincode {pincode} · Same-pincode first</> : ""}</div>
        </div>
        {type === "hiring" && (
          <button onClick={postJob} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2.5 text-sm font-semibold min-h-[44px]" data-testid="hiring-post-job-btn">
            <Plus className="w-4 h-4" /> Job post karein <span className="text-[10px] font-bold uppercase bg-white/20 rounded-full px-1.5 py-0.5">Free</span>
          </button>
        )}
      </div>
      {type !== "hiring" && <div className="mb-6"><RadiusFilter /></div>}
      {shared === "missing" && <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="shared-job-missing">Yeh job ab uplabdh nahi hai ya abhi approve nahi hui hai. Neeche baaki jobs dekhein.</div>}
      {shared && shared !== "missing" && (
        <div className="mb-8 scroll-mt-40" data-testid="shared-job">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Shared job</div>
          <div className="max-w-xl"><HireCard job={shared} defaultOpen /></div>
        </div>
      )}
      {type === "hiring" && !pincode && <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-900" data-testid="jobs-pin-hint">Apne paas ki jobs pehle dekhne ke liye upar header me apna PIN chunein.</div>}
      {type === "hiring" && <JobFilters items={items} value={jfEff} onChange={(v) => setJf((prev) => ({ ...v, km: prev.km }))} onKm={(km) => setJf((prev) => ({ ...prev, km }))} savedIds={savedIds} savedCount={user ? items.filter((j) => savedIds.has(j.id)).length : null} hasPin={!!pincode} />}
      {loading ? (
        <div className="text-center py-16 text-slate-500">Loading...</div>
      ) : loadError && items.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-600" data-testid="category-load-error">
          Server abhi busy hai, list load nahi hui.{" "}
          <button onClick={() => setReloadKey((k) => k + 1)} className="font-semibold text-emerald-700 underline" data-testid="category-retry-btn">Dobara try karein</button>
        </div>
      ) : type === "hiring" && items.length > 0 && shownItems.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500" data-testid="job-filters-empty">{jf.saved ? "Abhi koi saved job nahi hai. Kisi job par ♥ dabakar save karein." : jfEff.km ? `${jfEff.km} km ke andar koi job nahi mili. Doori badha kar dekhein.` : "In filters se koi job nahi mili. Filter hata kar dekhein."}</div>
      ) : (
        <div className={`grid gap-3 lg:gap-4 ${gridCols}`}>
          {shownItems.map(renderItem)}
        </div>
      )}
    </div>
  );
}
